import cn from 'classnames';
import React from 'react';
import Typography from '~components/typography';
import { SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings } from '~graphql/__generated__/SP_QueryStudentGetBookings';
import { useTranslation } from '~i18n';
import { Close, Tick } from '~svg-components';
import {
  useBookingDocumentCompleted,
  useBookingPaymentItemsIsPaid,
  useBookingReviewFormsInComplete,
  useDisplayArrivalSlot,
  useGetBookingPaymentItems,
  useGetBookingReviewForm,
  useGetBookingTaskCompletedStatus,
} from './booking-card-helper';

interface IProps {
  booking: SP_QueryStudentGetBookings_studentViewer_studentInfo_bookings;
  contact: any;
}
const BookingCardConfrim: React.FC<IProps> = ({ booking, contact }) => {
  const { t } = useTranslation(['student_portal']);

  const { documents, arrivalInformation } = booking;

  const isDisplayArrivalSlot = useDisplayArrivalSlot(booking);

  const isDocumentCompleted = useBookingDocumentCompleted(booking);

  const bookingPaymentItemsNeedToPay = useGetBookingPaymentItems(booking);
  const isPaymentItemAllPaid = useBookingPaymentItemsIsPaid(bookingPaymentItemsNeedToPay);

  const bookingReviewForms = useGetBookingReviewForm(booking);

  const isBookingReviewFormInComplete = useBookingReviewFormsInComplete(bookingReviewForms);

  const bookingTaskCompletedStatus = useGetBookingTaskCompletedStatus(booking, contact);

  return (
    <div className="booking-card-confirm">
      <div className="booking-card-confirm__info booking-card-confirm__info--complementary">
        <Typography component="div" variant="body3">
          {t('my_stay.task_completed')}{' '}
          {t('booking_card_task.of', {
            completed: bookingTaskCompletedStatus.completed,
            total: bookingTaskCompletedStatus.task,
          })}
        </Typography>
        <div className="booking-card-confirm__info-list">
          {bookingPaymentItemsNeedToPay.length > 0 && (
            <div className="booking-card-confirm__info-item">
              <div
                className={cn('booking-card-confirm__icon-bg', {
                  'booking-card-confirm__icon-bg--completed': isPaymentItemAllPaid,
                })}
              >
                {isPaymentItemAllPaid ? (
                  <Tick fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                ) : (
                  <Close fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                )}
              </div>

              <Typography component="div" variant="body3">
                {t('payment')}
              </Typography>
            </div>
          )}
          {documents && documents.length > 0 && (
            <div className="booking-card-confirm__info-item">
              <div
                className={cn('booking-card-confirm__icon-bg', {
                  'booking-card-confirm__icon-bg--completed': isDocumentCompleted,
                })}
              >
                {isDocumentCompleted ? (
                  <Tick fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                ) : (
                  <Close fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                )}
              </div>
              <Typography component="div" variant="body3">
                {t('my_stay.documentss')}
              </Typography>
            </div>
          )}
          <div className="booking-card-confirm__info-item">
            <div
              className={cn('booking-card-confirm__icon-bg', {
                'booking-card-confirm__icon-bg--completed': contact?.emergencyContacts,
              })}
            >
              {contact?.emergencyContacts ? (
                <Tick fill="var(--color-white)" className="booking-card-confirm__info-icon" />
              ) : (
                <Close fill="var(--color-white)" className="booking-card-confirm__info-icon" />
              )}
            </div>
            <Typography component="div" variant="body3">
              {t('check_in.emergency_contact.title')}
            </Typography>
          </div>
          {isDisplayArrivalSlot && (
            <div className="booking-card-confirm__info-item">
              <div
                className={cn('booking-card-confirm__icon-bg', {
                  'booking-card-confirm__icon-bg--completed': arrivalInformation,
                })}
              >
                {arrivalInformation ? (
                  <Tick fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                ) : (
                  <Close fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                )}
              </div>
              <Typography component="div" variant="body3">
                {t('check_in.arrvial_slot')}
              </Typography>
            </div>
          )}
          {bookingReviewForms.length > 0 && (
            <div className="booking-card-confirm__info-item">
              <div
                className={cn('booking-card-confirm__icon-bg', {
                  'booking-card-confirm__icon-bg--completed': !isBookingReviewFormInComplete,
                })}
              >
                {!isBookingReviewFormInComplete ? (
                  <Tick fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                ) : (
                  <Close fill="var(--color-white)" className="booking-card-confirm__info-icon" />
                )}
              </div>
              <Typography component="div" variant="body3">
                {t('form_and_disclamiers')}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <style jsx>
        {`
          .booking-card-confirm {
            margin-top: 14px;
            &__info {
              flex: 1;
              padding: 6px 0 6px 12px;
              position: relative;
              &--complementary {
                &:before {
                  content: '';
                  display: block;
                  width: 4px;
                  height: 100%;
                  position: absolute;
                  top: 0;
                  left: 0;
                  border-radius: 100px;
                  background-color: var(--color-blue_6);
                }
              }
            }
            &__icon-bg {
              width: 16px;
              height: 16px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 8px;
              background: var(--color-red_6);
              &--completed {
                background: var(--color-green_6);
              }
            }
            &__info-list {
              width: 510px;
              display: flex;
              flex-wrap: wrap;
            }
            &__info-item {
              margin-top: 8px;
              margin-right: 20px;
              display: flex;
              align-items: center;
            }
            :global(&__info-icon) {
              width: 14px;
              height: 12px;
            }
          }
          @media screen and (max-width: 768px) {
            .booking-card-confirm {
              &__info-list {
                width: 100%;
                display: block;
              }
              &__info-item {
                width: 100%;
                margin-right: 0;
              }
            }
          }
        `}
      </style>
    </div>
  );
};

export default BookingCardConfrim;
